/**
 * @file Contact.tsx
 * @description Contact page with office information and a simple contact form.
 */

import { FormEvent, useState } from 'react'

/**
 * ContactPage component provides contact details and a basic inquiry form.
 */
export default function ContactPage() {
  const [isSubmitted, setIsSubmitted] = useState(false)

  /**
   * Handles form submission by preventing default navigation.
   * In production this would send data to a backend or third-party service.
   */
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setIsSubmitted(true)
  }

  return (
    <div className="bg-slate-950">
      <section className="mx-auto max-w-5xl px-4 pb-16 pt-10 md:px-6 md:pt-14">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-50 md:text-4xl">
          Contact Us
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-slate-300 md:text-base">
          Whether you are exploring Agentic AI for the first time or scaling an
          existing initiative, the Ultimind team is ready to help.
        </p>

        <div className="mt-10 grid gap-10 md:grid-cols-[1.4fr_1fr]">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            {isSubmitted ? (
              <div className="flex h-full flex-col items-center justify-center py-10 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-green-500"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h2 className="mb-2 text-xl font-semibold text-slate-50">
                  Message Sent!
                </h2>
                <p className="mb-6 max-w-sm text-sm text-slate-300">
                  Thank you for reaching out. We have received your inquiry and
                  will get back to you within two business days.
                </p>
                <button
                  onClick={() => setIsSubmitted(false)}
                  className="rounded-full bg-slate-800 px-6 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-700"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <h2 className="text-lg font-semibold text-slate-50 md:text-xl">
                  Send us a message
                </h2>
                <p className="mt-2 text-xs text-slate-300 md:text-sm">
                  Share a bit about your use case and we will respond within two
                  business days.
                </p>
                <div className="mt-5 space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-xs font-medium text-slate-200 md:text-sm"
                    >
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 outline-none ring-sky-500/40 placeholder:text-slate-500 focus:border-sky-500 focus:ring-2"
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-xs font-medium text-slate-200 md:text-sm"
                      >
                        Work email
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 outline-none ring-sky-500/40 placeholder:text-slate-500 focus:border-sky-500 focus:ring-2"
                        placeholder="you@company.com"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-xs font-medium text-slate-200 md:text-sm"
                      >
                        Company
                      </label>
                      <input
                        id="company"
                        name="company"
                        type="text"
                        className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 outline-none ring-sky-500/40 placeholder:text-slate-500 focus:border-sky-500 focus:ring-2"
                        placeholder="Organization name"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-xs font-medium text-slate-200 md:text-sm"
                    >
                      How can we help?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-50 outline-none ring-sky-500/40 placeholder:text-slate-500 focus:border-sky-500 focus:ring-2"
                      placeholder="Briefly describe your use case, timelines, and success criteria."
                    />
                  </div>
                  <button
                    type="submit"
                    className="mt-2 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 to-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-sky-500/40 transition hover:from-sky-400 hover:to-blue-500"
                  >
                    Submit inquiry
                  </button>
                </div>
              </form>
            )}
          </div>

          <aside className="space-y-6 text-sm text-slate-300">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <h2 className="text-sm font-semibold text-sky-300 md:text-base">
                Singapore Headquarters
              </h2>
              <p className="mt-2 text-xs text-slate-300 md:text-sm">
                Ultimind Pte. Ltd.
                <br />
                Singapore
              </p>
              <p className="mt-3 text-xs text-slate-300 md:text-sm">
                Email: contact@ultimind.ai
              </p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
              <h2 className="text-sm font-semibold text-sky-300 md:text-base">
                Hangzhou R&amp;D Center
              </h2>
              <p className="mt-2 text-xs text-slate-300 md:text-sm">
                Hangzhou, China
                <br />
                In collaboration with the Institute of Computing Innovation,
                Zhejiang University.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  )
}
